import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { FileText, ExternalLink, Home } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportsPage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-secondary text-secondary-foreground';
      case 'in_progress':
        return 'bg-warning text-white';
      case 'completed':
        return 'bg-success text-white';
      case 'approved':
        return 'bg-primary text-white';
      default:
        return 'bg-secondary';
    }
  };

  const handleReportClick = (report) => {
    if (report.id) {
      navigate(`/reports/${report.id}`);
    } else {
      toast.error('Unable to open report - ID unavailable');
    }
  };

  const handleInspectionClick = (e, inspectionId) => {
    e.stopPropagation();
    if (inspectionId) {
      navigate(`/inspections/${inspectionId}`);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2" data-testid="reports-title">Reports</h1>
          <p className="text-muted-foreground">Track inspection reports and documentation</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : reports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-muted-foreground">No reports found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="reports-list">
            {reports.map((report, index) => {
              const hasValidId = report.id && report.id !== '';
              const hasInspectionId = report.inspection_id && report.inspection_id !== '';

              return (
                <motion.div
                  key={report.id || index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className={`transition-all duration-300 ${
                      hasValidId 
                        ? 'hover:shadow-lg hover:border-primary/50 cursor-pointer group' 
                        : 'opacity-75 cursor-not-allowed'
                    }`}
                    onClick={() => handleReportClick(report)}
                    data-testid={`report-card-${report.id || index}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                          <FileText className="h-8 w-8 text-accent flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <h3 
                              className={`font-medium truncate ${
                                hasValidId 
                                  ? 'group-hover:text-primary group-hover:underline transition-colors' 
                                  : ''
                              }`}
                              data-testid={`report-title-${report.id || index}`}
                            >
                              {report.title || 'Untitled Report'}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>Updated: {new Date(report.updated_at).toLocaleDateString()}</span>
                              {!hasValidId && (
                                <span className="text-destructive text-xs">• ID Unavailable</span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {/* Open Inspection Button */}
                          {hasInspectionId && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => handleInspectionClick(e, report.inspection_id)}
                              data-testid={`open-inspection-btn-${report.id || index}`}
                            >
                              <Home className="h-3 w-3 mr-1" />
                              Inspection
                            </Button>
                          )}
                          
                          {/* Open Report Button */}
                          {hasValidId && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/reports/${report.id}`);
                              }}
                              data-testid={`open-report-btn-${report.id}`}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Open
                            </Button>
                          )}
                          
                          <Badge 
                            className={getStatusColor(report.status)} 
                            data-testid={`report-status-${report.id || index}`}
                          >
                            {report.status?.replace('_', ' ') || 'Unknown'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
